import { useState } from "react";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Link,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { PsnsRedriveApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import type {
  PsnsRedriveMcmAttachResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import { getApiErrorMessage } from "./apiErrorMessage";
import {
  AssigneeAutosuggest,
  normaliseAlias,
  useAssigneeDirectory,
} from "../investigations/AssigneeAutosuggest";

interface Props {
  stage: Stage;
  s3Uri: string;
  etag: string;
  initialMcmId?: string;
  disabled: boolean;
  onChecked: (result: PsnsRedriveMcmAttachResult) => void;
}

const CHECK_LABELS: Array<
  [keyof PsnsRedriveMcmAttachResult["checks"], string]
> = [
  ["title", 'Title contains "PSNS redrive"'],
  ["fileLine", "Lists this file's line"],
  ["approvers", "Has at least one approver"],
  ["inProgress", "Status is In Progress"],
];

// Who is on call for PV FinSys TRACK. Lusca can't look this up itself yet, so the operator does.
export const PV_TRACK_ONCALL_URL =
  "https://oncall.corp.amazon.com/#/view/pv-finsys-track/schedule";

// The line the PSNS redrive validator looks for; S3 ETag quotes are stripped.
export const psnsMcmFileLine = (s3Uri: string, etag: string) =>
  `Redriving: "${s3Uri} ${etag.replace(/"/g, "")}"`;

export function PsnsRedriveAttachMcm({
  stage,
  s3Uri,
  etag,
  initialMcmId,
  disabled,
  onChecked,
}: Props) {
  const [mcmId, setMcmId] = useState(initialMcmId ?? "");
  const [notifyAlias, setNotifyAlias] = useState("");
  // Only Lusca Search users can be notified; the picker lists them.
  const directory = useAssigneeDirectory(stage, true);
  // Not judged while the list is loading; the worker checks the alias again before sending.
  const aliasUnknown =
    !!notifyAlias.trim() &&
    !directory.isLoading &&
    !directory.isKnown(notifyAlias);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PsnsRedriveMcmAttachResult | null>(null);
  const fileLine = psnsMcmFileLine(s3Uri, etag);

  const handleCheck = async () => {
    setChecking(true);
    setError(null);
    const alias = normaliseAlias(notifyAlias);
    try {
      const response = await PsnsRedriveApiFactory().attachPsnsRedriveMcm(
        stage,
        {
          mcmId: mcmId.trim(),
          eventsS3Uri: s3Uri,
          eventsS3Etag: etag,
          notifyAlias: alias || undefined,
        }
      );
      setResult(response.data);
      onChecked(response.data);
    } catch (err) {
      setResult(null);
      setError(getApiErrorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  return (
    <SpaceBetween size="m">
      <Box variant="p">
        The MCM&apos;s title must contain &quot;PSNS redrive&quot;, and this
        line must be in its description or, for a file added after approval, in
        a comment by the MCM&apos;s requester or by an approver who approved it.
        Don&apos;t edit an approved MCM&apos;s description: that can send it
        back for approval.
      </Box>
      <FormField
        label="Line for this file"
        secondaryControl={
          <CopyToClipboard
            getContent={() => Promise.resolve(fileLine)}
            iconName="copy"
            ariaLabel="Copy the line for this file"
          />
        }
      >
        <Input value={fileLine} readOnly />
      </FormField>
      <FormField label="MCM ID">
        <Input
          value={mcmId}
          onChange={({ detail }) => setMcmId(detail.value)}
          placeholder="MCM-123456"
        />
      </FormField>
      <FormField
        label="Notify on-call (optional)"
        errorText={aliasUnknown ? "Not a Lusca Search user" : undefined}
        description={
          <>
            Alias to email and Slack about this MCM, usually the current{" "}
            <Link href={PV_TRACK_ONCALL_URL} external>
              PV FinSys TRACK on-call
            </Link>
            . Must be a Lusca Search user.
          </>
        }
      >
        <AssigneeAutosuggest
          directory={directory}
          value={notifyAlias}
          onChange={setNotifyAlias}
          ariaLabel="On-call alias to notify"
          placeholder="Start typing an alias"
        />
      </FormField>
      <Button
        onClick={() => {
          handleCheck().catch((unexpected: unknown) =>
            setError(getApiErrorMessage(unexpected))
          );
        }}
        loading={checking}
        disabled={disabled || checking || !mcmId.trim() || aliasUnknown}
        data-testid="check-existing-mcm"
      >
        {result ? "Check again" : "Check MCM"}
      </Button>
      {disabled && (
        <Box variant="small" color="text-status-inactive">
          Run the dry run first.
        </Box>
      )}
      {error && (
        <Alert type="error" header="MCM check failed">
          {error}
        </Alert>
      )}
      {result && (
        <Alert
          type={result.usable ? "success" : "warning"}
          header={
            result.usable
              ? `${result.mcmId} can be used for this redrive`
              : `${result.mcmId} can't be used yet`
          }
        >
          <SpaceBetween size="xs">
            <Link
              href={result.mcmUrl}
              external
              externalIconAriaLabel="Opens MCM in a new tab"
            >
              Open {result.mcmId}
            </Link>
            {result.mcmStatus && <Box>Status: {result.mcmStatus}</Box>}
            {CHECK_LABELS.map(([key, label]) => (
              <StatusIndicator
                key={key}
                type={result.checks[key] ? "success" : "error"}
              >
                {label}
              </StatusIndicator>
            ))}
            {result.fileLineSource === "comment" && (
              <Box>
                This file was added in a comment by the MCM&apos;s requester or
                by an approver who approved it.
              </Box>
            )}
            {!result.checks.fileLine && (
              <Box>
                This MCM doesn&apos;t list this file, so it can&apos;t approve
                this redrive yet. As its requester or an approver who approved
                it, add the line above as a comment, then check again. Comments
                only count once the MCM is approved.
              </Box>
            )}
            {result.checks.fileLine && !result.checks.inProgress && (
              <Box>
                Once it is approved, start it in MCM (In Progress), then check
                again.
              </Box>
            )}
            {result.commented && (
              <Box>
                This file&apos;s dry run was added to the MCM as a comment.
              </Box>
            )}
            {result.warning && <Box>{result.warning}</Box>}
            {result.notifyQueued && (
              <Box>{result.notifyAlias} will be notified about this MCM.</Box>
            )}
            {result.notifyWarning && <Box>{result.notifyWarning}</Box>}
          </SpaceBetween>
        </Alert>
      )}
    </SpaceBetween>
  );
}
